import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageContainer from "../components/common/PageContainer";
import ChecklistDetail from "../components/checklist/ChecklistDetail";
import ChecklistForm from "../components/checklist/ChecklistForm";
import LoadingState from "../components/common/LoadingState";
import ErrorState from "../components/common/ErrorState";

/**
 * Page component for viewing and editing a checklist
 */
const ChecklistView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // In a real application, we would fetch the checklist data from the API
    // For now, we'll simulate a fetch with a timeout
    const fetchChecklist = async () => {
      try {
        setLoading(true);

        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Mock data
        const mockChecklist = {
          id,
          title: "Sample Checklist",
          description: "This is a sample checklist for demonstration purposes.",
          items: [
            { id: "item-1", text: "First item", completed: true },
            { id: "item-2", text: "Second item", completed: false },
            { id: "item-3", text: "Third item", completed: false },
          ],
        };

        setChecklist(mockChecklist);
        setError(null);
      } catch (err) {
        setError("Failed to load checklist. Please try again.");
        console.error("Error fetching checklist:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchChecklist();
  }, [id]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleDelete = (checklistId) => {
    // In a real application, we would delete the checklist via the API
    console.log("Deleting checklist:", checklistId);

    // Navigate back to the checklists page
    navigate("/checklists");
  };

  const handleDuplicate = (checklistId) => {
    // In a real application, we would duplicate the checklist via the API
    console.log("Duplicating checklist:", checklistId);

    // Navigate to the new checklist
    navigate("/checklists/new");
  };

  const handleUpdateItem = (checklistId, itemId, updates) => {
    // In a real application, we would update the item via the API
    console.log(
      "Updating item:",
      itemId,
      "in checklist:",
      checklistId,
      "with:",
      updates
    );

    // Update the local state
    setChecklist((prevChecklist) => ({
      ...prevChecklist,
      items: prevChecklist.items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item
      ),
    }));
  };

  const handleSave = (updatedChecklist) => {
    // In a real application, we would save the checklist to the API
    console.log("Saving updated checklist:", updatedChecklist);

    // Update the local state
    setChecklist(updatedChecklist);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (loading) {
    return (
      <PageContainer title="Loading Checklist...">
        <LoadingState />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title="Error">
        <ErrorState message={error} onRetry={() => window.location.reload()} />
      </PageContainer>
    );
  }

  if (!checklist) {
    return (
      <PageContainer title="Checklist Not Found">
        <ErrorState
          message="The requested checklist could not be found."
          onRetry={() => navigate("/checklists")}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={isEditing ? "Edit Checklist" : checklist.title}
      breadcrumbs={[
        { text: "Checklists", path: "/checklists" },
        { text: isEditing ? "Edit Checklist" : checklist.title },
      ]}
    >
      {isEditing ? (
        <ChecklistForm
          checklist={checklist}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      ) : (
        <ChecklistDetail
          checklist={checklist}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onUpdateItem={handleUpdateItem}
        />
      )}
    </PageContainer>
  );
};

export default ChecklistView;
